import { useMemo } from 'react';
import { type RootState } from '@/store';
import { useSelector } from 'react-redux';

import {
  useGetCurrentUserQuery,
  useGetUserInterestRateQuery,
} from '@/apis/extended/userApi';
import {
  calculatePurchasingPower,
  estimatePurchasingPower,
  type PurchasingPowerBreakdown,
} from '@/utils/calculator';
import { UserLoanInfo } from '@/types/UserType';

export const usePurchasingPowerEstimation = (_loanInfo?: UserLoanInfo) => {
  const { data: interestRate } = useGetUserInterestRateQuery();
  const { data: currentUser } = useGetCurrentUserQuery();
  const interestRateState = useSelector(
    (state: RootState) => state.interestRate
  );

  const purchasingPower = useMemo(() => {
    const loanInfo = _loanInfo || currentUser?.loanInfo;
    if (!loanInfo || !interestRate) return 0;

    const { monthlyDebt, monthlyIncome, creditScore, downPayment, loanTerm } =
      loanInfo;
    if (!monthlyIncome) return 0;

    // Create a minimal state object with only what's needed
    const minimalState = {
      interestRate: interestRateState,
    } as RootState;

    return calculatePurchasingPower(
      {
        monthlyIncome,
        creditScore,
        downPayment,
        monthlyDebt,
        loanTerm,
        interestRate,
        // GET /user/interest-rate is priced for this user's program + credit
        interestRateIsPriced: true,
      },
      minimalState
    );
  }, [_loanInfo, currentUser, interestRate, interestRateState]);

  return purchasingPower;
};

/** Same calculation as usePurchasingPowerEstimation, with the full PITI/DTI assumption breakdown for inline disclosure. */
export const usePurchasingPowerBreakdown = (
  _loanInfo?: UserLoanInfo
): PurchasingPowerBreakdown | null => {
  const { data: interestRate } = useGetUserInterestRateQuery();
  const { data: currentUser } = useGetCurrentUserQuery();
  const interestRateState = useSelector(
    (state: RootState) => state.interestRate
  );

  return useMemo(() => {
    const loanInfo = _loanInfo || currentUser?.loanInfo;
    if (!loanInfo || !interestRate || !loanInfo.monthlyIncome) return null;

    const { monthlyDebt, monthlyIncome, creditScore, downPayment, loanTerm } =
      loanInfo;

    const minimalState = {
      interestRate: interestRateState,
    } as RootState;

    return estimatePurchasingPower(
      {
        monthlyIncome,
        creditScore,
        downPayment,
        monthlyDebt,
        loanTerm,
        interestRate,
        // GET /user/interest-rate is priced for this user's program + credit
        interestRateIsPriced: true,
      },
      minimalState
    );
  }, [_loanInfo, currentUser, interestRate, interestRateState]);
};
